<template>
  <div class="preferences-settings">
    <div class="settings-title">
      <span class="title-text">
        <el-text
          size="large"
          class="preferences-nav-title"
        >
          {{ $t('views.account_center.preferences') }}
        </el-text>
      </span>
    </div>
    <div class="preferences-content">
      <!-- 外观设置 -->
      <div class="preference-section">
        <div class="section-left">
          <div class="section-title"><el-text>{{ $t('views.account_center.appearance') }}</el-text></div>
          <div class="section-desc"><el-text
            type="info"
            size="small"
          >{{ $t('views.account_center.theme_customization') }}</el-text></div>
        </div>
        <div class="section-right">
          <template v-if="isLoading">
            <div class="dropdown-skeleton">
              <el-skeleton animated>
                <template #template>
                  <el-skeleton-item
                    variant="text"
                    class="skeleton-dropdown"
                  />
                </template>
              </el-skeleton>
            </div>
          </template>
          <template v-else>
            <el-popover
              v-model:visible="themePopoverVisible"
              placement="bottom"
              width="110"
              :show-arrow="false"
              trigger="click"
              popper-class="custom-popover"
              :popper-options="{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [-30, 6]
                    }
                  }
                ]
              }"
            >
              <template #reference>
                <div
                  class="dropdown-link"
                  :class="{'active': themePopoverVisible}"
                >
                  <el-text>{{ currentTheme.name }}</el-text>
                  <el-icon><ArrowDown /></el-icon>
                </div>
              </template>
              <div class="popover-content">
                <el-radio-group
                  v-model="themeValue"
                  size="default"
                >                  <el-radio
                  v-for="item in themeOptions"
                  :key="item.id"
                  :value="item.key"
                  @change="handleThemeChange()"
                >
                  {{ item.name }}
                </el-radio>
                </el-radio-group>
              </div>
            </el-popover>
          </template>
        </div>
      </div>

      <!-- 语言设置 -->
      <div class="preference-section-language">
        <div class="section-left">
          <div class="section-title"><el-text>{{ $t('views.account_center.language') }}</el-text></div>
          <div class="section-desc"><el-text
            type="info"
            size="small"
          >{{ $t('views.account_center.change_language') }}</el-text></div>
        </div>
        <div class="section-right">
          <template v-if="isLoading">
            <div class="dropdown-skeleton">
              <el-skeleton animated>
                <template #template>
                  <el-skeleton-item
                    variant="text"
                    class="skeleton-dropdown"
                  />
                </template>
              </el-skeleton>
            </div>
          </template>
          <template v-else>
            <el-popover
              v-model:visible="languagePopoverVisible"
              placement="bottom"
              width="110"
              trigger="click"
              :show-arrow="false"
              popper-class="custom-popover"
              :popper-options="{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [-30, 6]
                    }
                  }
                ]
              }"
            >
              <template #reference>
                <div
                  class="dropdown-link"
                  :class="{'active': languagePopoverVisible}"
                >
                  <el-text>{{ currentLanguage.name }}</el-text>
                  <el-icon><ArrowDown /></el-icon>
                </div>
              </template>
              <div class="popover-content">
                <el-radio-group
                  v-model="languageValue"
                  size="default"
                >
                  <el-radio
                    v-for="item in languageOptions"
                    :key="item.id"
                    :value="item.key"
                    @change="savePreferences()"
                  >
                    {{ item.name }}
                  </el-radio>
                </el-radio-group>
              </div>
            </el-popover>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ArrowDown } from '@element-plus/icons-vue'
import { getUserPreference, getPreferenceConfigs, updateUserPreference} from '@/api/user'
import i18n, { setLocale, setI18nLanguage, getLocale } from '@/locales/i18n'
import getPageTitle from '@/utils/pageTitle'
import { switchTheme, getTheme, getThemeKeyById } from '@/utils/theme'
import EventBus from "@/utils/eventBus";

export default {
  name: 'Preferences',
  components: {
    ArrowDown
  },
  data() {
    return {
      themeValue: 'system',
      languageValue: 'zh-CN',
      themePopoverVisible: false,
      languagePopoverVisible: false,
      themeOptions: [],
      languageOptions: [],
      isLoading: true
    }
  },
  computed: {
    currentTheme() {
      return this.themeOptions.find(item => item.key === this.themeValue) || {}
    },
    currentLanguage() {
      return this.languageOptions.find(item => item.key === this.languageValue) || {}
    }
  },
  watch: {
  },
  mounted() {
    // 初始化主题值
    this.themeValue = getTheme()
    // 这里添加获取用户当前偏好设置的逻辑
    this.loadPreferences()
    this.getCurrentPreferences()
  },
  methods: {
    loadPreferences() {
      getPreferenceConfigs()
        .then(({ code, data }) => {
          if (code === 0) {
            this.languageOptions = data.find(c => c.key === 'language')?.data || []
            const order = { 'en-us': 1, 'zh-cn': 2, 'zh-tw': 3 }
            this.languageOptions.sort((a, b) => (order[a.key] || 99) - (order[b.key] || 99))

            // 获取原始主题选项数据
            const themeOptionsData = data.find(c => c.key === 'theme')?.data || []

            // 使用翻译替换名称
            this.themeOptions = themeOptionsData.map(theme => {
              return {
                ...theme,
                name: this.$t(`views.account_center.${theme.key}`)
              }
            })
          }
        })
        .catch(e => {
          console.error(this.$t('views.account_center.config_load_failed'), e)
        })
    },
    getCurrentPreferences() {
      this.isLoading = true
      getUserPreference()
        .then(({ code, data }) => {
          if (code === 0) {
            // 从后端获取主题设置并转换为对应的key
            const themeKey = getThemeKeyById(data.theme_id)
            this.themeValue = themeKey
            this.languageValue = data.language_key
          }
        })
        .catch(e => {
          console.error(this.$t('views.account_center.user_settings_load_failed'), e)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    savePreferences() {
      const params = {
        theme_id: this.themeOptions.find(t => t.key === this.themeValue)?.id,
        language_id: this.languageOptions.find(l => l.key === this.languageValue)?.id
      }
      // 检查是否有语言变更
      const localeLanguage = getLocale()

      updateUserPreference(params).then(response => {
        if (localeLanguage !== this.languageValue) {
          this.applyLanguageChange(this.languageValue)
          window.location.reload()
        }
      })
        .catch(error => {
          console.error(this.$t('views.account_center.save_failed'), error)
        })
    },
    // 处理主题变更
    handleThemeChange() {
      // 应用主题变更，传入 store
      const actual = switchTheme(this.themeValue, this.$store)
      EventBus.emit('blocknote-theme-change', actual)
      this.savePreferences()
    },
    // 应用语言变更
    async applyLanguageChange(langKey) {
      // 暂时先注释 本地有配置文件 后续更改为从后端获取
      await setLocale(i18n, langKey)
      setI18nLanguage(i18n, langKey)
      document.title = getPageTitle(this.$t(this.$route.meta.title))

      if (this.themeOptions.length > 0) {
        this.themeOptions = this.themeOptions.map(theme => {
          return {
            ...theme,
            name: this.$t(`views.account_center.${theme.key}`)
          }
        })
      }
    }
  }
}
</script>

<style lang="scss">
.custom-popover{
  min-width: 0 !important;
  min-height: 130px !important;
  font-family: Helvetica Neue, Helvetica, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Arial, sans-serif;
}

</style>

<style scoped lang="scss">
@import '@/assets/css/views/account_center/components/preferences';
</style>
